import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Gauge, Package, Bell, Users, ShieldCheck, SignOut, List, Bread, Gear, Key } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import ChangePassword from "@/components/ChangePassword";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, end: true },
  { to: "/products", label: "Products", icon: Package },
  { to: "/notifications", label: "Notifications", icon: Bell, perm: "notifications.manage" },
  { to: "/users", label: "Users", icon: Users, perm: "users.manage" },
  { to: "/roles", label: "Roles", icon: ShieldCheck, perm: "roles.manage" },
  { to: "/settings", label: "Settings", icon: Gear, perm: "settings.manage" },
];

function NavItems({ onNavigate }) {
  const { can } = useAuth();
  return (
    <nav className="flex flex-col gap-1 px-3" data-testid="sidebar-nav">
      {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-[#FFF7ED] text-[#C2410C] border-l-2 border-[#EA580C]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`
          }
        >
          <n.icon size={18} weight="duotone" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 py-5 border-b border-slate-200" data-testid="brand-logo">
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#EA580C]">
        <Bread size={20} weight="duotone" className="text-white" />
      </div>
      <div>
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900 leading-none">BakeFlow</div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Product Pipeline</div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-200 bg-white lg:block" data-testid="sidebar">
        <Brand />
        <div className="mt-4"><NavItems /></div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6" data-testid="topbar">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" className="rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden">
                  <List size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <Brand />
                <div className="mt-4"><NavItems onNavigate={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <span className="font-heading text-base font-bold text-slate-900 lg:hidden">BakeFlow</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-slate-900" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs capitalize text-slate-500" data-testid="current-user-role">{user?.role}</div>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF7ED] text-sm font-bold text-[#C2410C]">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <button data-testid="change-password-button" onClick={() => setPwOpen(true)} title="Change password" className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
              <Key size={20} />
            </button>
            <button data-testid="logout-button" onClick={handleLogout} className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
              <SignOut size={20} />
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
      <ChangePassword open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
